import { useEffect, useId, useRef, type ChangeEvent, type KeyboardEvent } from "react";
import { cx } from "@hamlak/ui";
import { t } from "@/i18n/ko";
import "./dateField.css";

export type DateParts = { year: string; month: string; day: string };

export const EMPTY_DATE: DateParts = { year: "", month: "", day: "" };

/** 형식만 검사한다(8자리·실존 날짜). 나이 판정은 서버. 유효하면 YYYY-MM-DD, 아니면 null. */
export function toIsoDate({ year, month, day }: DateParts): string | null {
  if (!/^\d{4}$/.test(year) || !/^\d{1,2}$/.test(month) || !/^\d{1,2}$/.test(day)) return null;
  const y = Number(year);
  const m = Number(month);
  const d = Number(day);
  if (y < 1900 || m < 1 || m > 12 || d < 1 || d > 31) return null;
  const probe = new Date(Date.UTC(y, m - 1, d));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== m - 1 || probe.getUTCDate() !== d) return null;
  return `${year}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

type Props = {
  value: DateParts;
  onChange: (next: DateParts) => void;
  error?: string | undefined;
  disabled?: boolean | undefined;
  /** 마운트 시 연도 칸에 포커스(AC0-232) */
  autoFocus?: boolean | undefined;
};

const LIMITS: Record<keyof DateParts, number> = { year: 4, month: 2, day: 2 };
const ORDER: (keyof DateParts)[] = ["year", "month", "day"];

/** 연·월·일 3칸(시안 444:3788: 칸마다 라벨, 연도 150 + 월·일 flex). 숫자 키패드, 자릿수가 차면 자동으로 다음 칸. 입력값은 메모리 외 저장 금지. */
export function DateField({ value, onChange, error, disabled, autoFocus }: Props) {
  const id = useId();
  const refs = useRef<Record<keyof DateParts, HTMLInputElement | null>>({ year: null, month: null, day: null });
  useEffect(() => {
    if (autoFocus) refs.current.year?.focus();
    // 마운트 1회만 — 이후 입력 중 포커스 이동은 handle·onKeyDown 이 맡는다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handle = (key: keyof DateParts) => (e: ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, LIMITS[key]);
    onChange({ ...value, [key]: digits });
    if (digits.length === LIMITS[key]) {
      const next = ORDER[ORDER.indexOf(key) + 1];
      if (next) refs.current[next]?.focus();
    }
  };
  const onKeyDown = (key: keyof DateParts) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && value[key] === "") {
      const prev = ORDER[ORDER.indexOf(key) - 1];
      if (prev) refs.current[prev]?.focus();
    }
  };

  return (
    <div className="date-field" role="group" aria-label={t("birthDate.label")}>
      <div className={cx("date-field__row", error && "date-field__row--error")}>
        {ORDER.map((key) => (
          <div key={key} className={cx("date-field__cell", key === "year" && "date-field__cell--year")}>
            <label htmlFor={`${id}-${key}`} className="date-field__label">
              {t(key === "year" ? "birthDate.year" : key === "month" ? "birthDate.month" : "birthDate.day")}
            </label>
            <input
              id={`${id}-${key}`}
              ref={(el) => {
                refs.current[key] = el;
              }}
              className="date-field__input"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              value={value[key]}
              maxLength={LIMITS[key]}
              disabled={disabled}
              onChange={handle(key)}
              onKeyDown={onKeyDown(key)}
              aria-invalid={error ? true : undefined}
            />
          </div>
        ))}
      </div>
      <span className="date-field__message" role={error ? "alert" : undefined}>
        {error ?? " "}
      </span>
    </div>
  );
}
